import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { MatchesListPage } from '@ui/fixtures/components/MatchesListPage/MatchesListPage';
import { useIsMobile } from '../../utils/useIsMobile';
import { getCurrentMonth, getMonth, getPreviousMonth, getUTCDate, MonthData } from '@ui/utils/date';
import { MatchPeriodFilter, MatchSort, MatchStatusFilter } from '@repo/types';
import { useMatchFiltersQuery } from '@data/matches/useMatchFiltersQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { Helmet } from 'react-helmet-async';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';
import { useTenantFeatureFlagsQuery } from '@data/tenants/useTenantFeatureFlagsQuery';
import { useEditableClubsQuery } from '@data/clubs/useEditableClubsQuery';
import { useMatchDatesQuery } from '@data/matches/useMatchDatesQuery';

const getStartDate = (startDate: string | null) => {
  if (!startDate) {
    return getCurrentMonth();
  } else {
    const jsDate = new Date(startDate);
    const d = getUTCDate(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());

    return {
      ...getMonth(d),
      date: d,
    };
  }
};

export function ResultsPage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateSearchParam = searchParams.get('startDate');
  const endDateSearchParam = searchParams.get('endDate');
  const teamSearchParam = searchParams.get('team');
  const competitionSearchParam = searchParams.get('competition');
  const [updatedMonthToShow, setUpdatedMonthToShow] = useState<MonthData | null>(null);
  const currentMonth = useMemo(() => getStartDate(startDateSearchParam), [startDateSearchParam]);

  const { data: clubs, isLoading: areClubsLoading } = useEditableClubsQuery();
  const { data: matchFilters, isLoading: matchFiltersLoading } = useMatchFiltersQuery();
  const { data: tenantFeatureFlags, isLoading: isLoadingFeatureFlags } = useTenantFeatureFlagsQuery();

  const selectedCompetition = useMemo(() => {
    return matchFilters?.find((matchFilter) => matchFilter.competitions === competitionSearchParam);
  }, [matchFilters, competitionSearchParam]);

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const startDate = useMemo(() => {
    if (startDateSearchParam) {
      return startDateSearchParam;
    }

    return currentMonth.startDate;
  }, [startDateSearchParam, currentMonth.startDate]);

  const endDate = useMemo(() => {
    if (endDateSearchParam) {
      return endDateSearchParam;
    }

    return today < currentMonth.endDate ? today : currentMonth.endDate;
  }, [endDateSearchParam, today, currentMonth.endDate]);

  const {
    data: matches,
    isLoading: matchesLoading,
    isFetchedAfterMount: areMatchesFetchedAfterMount,
    error: matchesError,
  } = useMatchesQuery(
    {
      limit: 1000,
      period: MatchPeriodFilter.Custom,
      startDate,
      endDate,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      teamId: teamSearchParam,
      competition: selectedCompetition?.competitions ?? matchFilters?.[0]?.competitions,
    },
    {
      enabled: !!matchFilters,
    }
  );

  const { data: matchDates, isLoading: matchDatesLoading } = useMatchDatesQuery(
    {
      endDate: today,
      status: MatchStatusFilter.RESULT,
      teamId: teamSearchParam,
      competition: selectedCompetition?.competitions ?? matchFilters?.[0]?.competitions,
    },
    {
      enabled: !!matchFilters,
    }
  );

  // const matchesWithoutPostponed = useMemo(() => {
  //   const today = startOfDay(new Date());

  //   return matches?.items.filter((match) => {
  //     if (match.status !== MatchStatus.Postponed && match.status !== MatchStatus.Abandoned) {
  //       return true;
  //     }

  //     const matchDate = new Date(match.date);

  //     if (compareAsc(today, matchDate) <= 0) {
  //       return true;
  //     }
  //     return false;
  //   });
  // }, [matches]);

  useEffect(() => {
    if (
      !matchesLoading &&
      areMatchesFetchedAfterMount &&
      !startDateSearchParam &&
      (!matches || matches?.items?.length === 0)
    ) {
      const prevMonth = getPreviousMonth(getCurrentMonth().date);
      setSearchParams(
        (prev) => {
          prev.set('startDate', prevMonth.startDate);
          prev.set('endDate', prevMonth.endDate);
          return prev;
        },
        { replace: true }
      );
      setUpdatedMonthToShow(prevMonth);
    }
  }, [
    areMatchesFetchedAfterMount,
    matchesLoading,
    matches?.items?.length,
    currentMonth,
    startDateSearchParam,
    matches,
    setSearchParams,
  ]);

  if (matchFiltersLoading || matchesLoading || matchDatesLoading || areClubsLoading || isLoadingFeatureFlags) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (matchesError || !matchFilters || !matches || !clubs || !tenantFeatureFlags) {
    return <NotFoundPage />;
  }

  const metaTitle = `${import.meta.env.VITE_SITE_NAME} Football Scores, Results & Season Archives`;
  const metaDescription = `View ${
    import.meta.env.VITE_SITE_NAME
  } scores, results & season archives, along with other competitions involving ${
    import.meta.env.VITE_SITE_NAME
  } clubs, on the official website of the ${import.meta.env.VITE_SITE_NAME}.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <MatchesListPage
        title={'Results'}
        isMobile={isMobile}
        matchFilters={matchFilters}
        selectedCompetition={selectedCompetition ?? matchFilters[0]}
        matches={matches?.items}
        matchDates={matchDates}
        clubs={clubs.items}
        currentMonth={updatedMonthToShow ?? currentMonth}
        monthSwitcherMode="RESULTS"
        featureFlags={tenantFeatureFlags}
        sortByDate={'DESC'}
      />
    </>
  );
}
